import IconCheck from '@apollo/icons/default/IconCheck.svg';
import IconCopy from '@apollo/icons/default/IconCopy.svg';
import {
  Button,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  Divider,
  IconButton,
} from '@apollo/orbit';
import classnames from 'classnames';
import copy from 'copy-to-clipboard';
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import * as orgRoutes from 'src/app/account/routes';
import { parseGraphRef } from 'src/app/graph/hooks/useGraphRef';
import * as graphRoutes from 'src/app/graph/routes';
import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { Loading } from 'src/components/common/loading/Loading';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { RouterHealthBadge } from 'src/components/routerHealthBadge/RouterHealthBadge';
import { useCloudRouterStatusQuery } from 'src/hooks/useCloudRouterStatusQuery';
import { triggerFullPageConfetti } from 'src/lib/confetti';
import Config from 'src/lib/config';
import { RouterStatus } from 'src/lib/graphqlTypes/types';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { AdditionalResourcesSection } from '../../AdditionalResourcesSection';
import {
  ButtonState,
  NewSupergraphOnboardingControls,
} from '../NewSupergraphOnboardingControls';
import { NewSupergraphOnboardingStepLayout } from '../NewSupergraphOnboardingStepLayout';
import {
  CommonCloudDedicatedProps,
  CommonStepProps,
  SupergraphOnboardingStepPrivate,
} from '../shared';

export type Props = React.ComponentProps<
  typeof PrivateSupergraphSummaryStepContent
> &
  Omit<
    React.ComponentProps<typeof NewSupergraphOnboardingStepLayout>,
    'description' | 'image' | 'children' | 'stepId'
  > & {
    data: {
      serviceId: string;
      variant: string;
      name: string;
      url: string | null;
    };
  };

export const PrivateSupergraphSummaryStep = ({
  setPreviousStep,
  previousStep,
  values,
  data,
  ...contentProps
}: Props) => {
  return (
    <NewSupergraphOnboardingStepLayout
      values={values}
      stepId={SupergraphOnboardingStepPrivate.Supergraph}
      previousStep={previousStep}
      setPreviousStep={setPreviousStep}
      className="mx-auto"
      hasPrivateSubgraphs={contentProps.hasPrivateSubgraphs}
    >
      <PrivateSupergraphSummaryStepContent
        data={data}
        {...contentProps}
        values={values}
      />
    </NewSupergraphOnboardingStepLayout>
  );
};

export const PrivateSupergraphSummaryStepContent = ({
  creatingSupergraph,
  continueAnalyticsLabelsByStep,
  whenStepsBegan,
  nextButtonState,
  data,
}: CommonStepProps &
  CommonCloudDedicatedProps & {
    nextButtonState: ButtonState;
    setNextButtonState: React.Dispatch<React.SetStateAction<ButtonState>>;
  } & {
    data: {
      serviceId: string;
      variant: string;
      name: string;
      url: string | null;
    };
  }) => {
  const [hasBeenPollingFor1Min, setHasBeenPollingFor1Min] = useState(false);
  const skip = !data.serviceId || !data.variant || hasBeenPollingFor1Min;
  const graphRef = data && parseGraphRef(`${data.serviceId}@${data.variant}`);

  const { data: routerStatusData, loading } = useCloudRouterStatusQuery(
    data.serviceId,
    data.variant,
    skip,
  );

  useEffect(() => {
    if (skip) {
      setHasBeenPollingFor1Min(false);
    } else {
      const timeout = setTimeout(() => {
        setHasBeenPollingFor1Min(true);
      }, 60_000);
      return () => clearTimeout(timeout);
    }
  }, [skip]);

  const [routerStatus, setRouterStatus] = useState<RouterStatus>(
    RouterStatus.CREATING,
  );

  useEffect(() => {
    if (routerStatusData?.service?.variant?.router) {
      setRouterStatus(routerStatusData?.service?.variant?.router?.status);
    }
  }, [
    loading,
    routerStatus,
    routerStatusData,
    routerStatusData?.service?.variant?.router,
  ]);

  useEffect(() => {
    if (routerStatus === RouterStatus.RUNNING) {
      triggerFullPageConfetti();
    }
  }, [routerStatus]);

  if (data?.name.length < 1 || (!!data?.url && data.url.length < 1)) {
    return <Loading />;
  }

  return (
    <div className="mx-24">
      <div className="mb-2 text-center text-2xl font-semibold">
        Congratulations on creating a single access point for your GraphQL
        architecture!
      </div>
      <div>
        <div className="mt-2 text-center text-xl">
          You just federated your GraphQL architecture. Easy, right?
        </div>
        <div className="flex flex-row justify-between">
          <Card className="mt-8 flex flex-1 flex-col px-6 py-8 lg:mr-10">
            <CardHeader>
              <CardTitle>Apollo’s GraphOS quick-start guide</CardTitle>
              <CardDescription>
                Here are the next best steps for GraphOS onboarding
              </CardDescription>
            </CardHeader>
            <Divider />
            <CardBody>
              <SummaryCardSection
                heading="Build your router instance for your GraphQL Service"
                description="Provide your endpoint for Apollo to provision and configure a router"
                completed
              />
              {/* Explorer CTA */}
              <Divider />
              <div className="flex flex-grow justify-between">
                <div className="mr-2 flex items-center pr-2">
                  <IconCheck className="mr-4" />
                  <div className="text-base">
                    <div className="text-lg font-semibold">
                      Try it out! Query with Explorer
                    </div>
                    <div className="text-base">
                      Execute your first query against your new router
                    </div>
                  </div>
                </div>
                {graphRef && (
                  <Button
                    id="runQueryButton"
                    variant="primary"
                    as={Link}
                    to={graphRoutes.explorerRouteConfig.location({
                      ...graphRef,
                    })}
                  >
                    Run a query
                  </Button>
                )}
              </div>
              {/* Observability CTA */}
              <Divider />
              <div className="flex flex-grow justify-between">
                <div className="mr-2 flex items-center pr-2">
                  <IconCheck className="mr-4" />
                  <div className="text-base">
                    <div className="text-lg font-semibold">
                      Visualize your GraphQL metrics
                    </div>
                    <div className="text-base">
                      The router auto-collects metrics on all operations
                    </div>
                  </div>
                </div>
                {graphRef && (
                  <Button
                    id="viewMetricsButton"
                    variant="primary"
                    as={Link}
                    to={graphRoutes.insightsRouteConfig.location({
                      ...graphRef,
                    })}
                  >
                    View Metrics
                  </Button>
                )}
              </div>
              {/* Private Subgraph CTA */}
              <Divider />
              <div className="flex flex-grow justify-between">
                <div className="mr-2 flex items-center pr-2">
                  <IconCheck className="mr-4" />
                  <div className="text-base">
                    <div className="text-lg font-semibold">
                      Further customize your Router settings
                    </div>
                    <div className="text-base">
                      Add more capacity, authorization, authentication, and more
                    </div>
                  </div>
                </div>
                <Button
                  id="customizeRouterButton"
                  variant="primary"
                  as={Link}
                  to={orgRoutes.userSettingsRouteConfig.location({
                    ...graphRef,
                  })}
                >
                  Configure
                </Button>
              </div>
            </CardBody>
          </Card>
          <Card className="mt-8 flex-1">
            <CardHeader>
              <CardTitle>Your Router</CardTitle>
            </CardHeader>
            <CardBody>
              {/* Your Router Section */}
              <div>
                <RouterHealthBadge
                  routerStatus={routerStatus}
                  isInitiating={true}
                />
              </div>
              {/* Router URL Section */}
              <div>
                <div className="mb-2 text-xl font-semibold">Router URL</div>
                <ClickableUrl
                  variantUrl={data.url ?? ''}
                  graphId={data.serviceId}
                  graphVariant={data.variant}
                />
                <span className="text-sm italic text-placeholder">
                  Query all data with a single request
                </span>
              </div>
              {/* Variant Section */}
              <div>
                <div className="text-xl font-semibold">GraphOS Variant</div>
                <span className="text-lg font-semibold text-secondary">
                  {data.name}
                </span>

                <div className="italic text-placeholder">
                  Apollo created this variant instance alongside your GraphOS
                  Router. This represents your Router environment.
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
        <AdditionalResourcesSection />
      </div>

      <div className="hidden items-center justify-between px-0 py-16 3xl:flex">
        <NewSupergraphOnboardingControls.CancelButton
          creatingSupergraph={creatingSupergraph}
        />

        <NewSupergraphOnboardingControls.BackButton
          creatingSupergraph={creatingSupergraph}
          className="ml-auto"
        />
        <NewSupergraphOnboardingControls.NextButton
          whenStepsBegan={whenStepsBegan}
          smallScreen={false}
          creatingSupergraph={creatingSupergraph}
          state={nextButtonState}
          continueAnalyticsLabelsByStep={continueAnalyticsLabelsByStep}
        />
      </div>
    </div>
  );
};

const SummaryCardSection = ({
  heading,
  description,
  actionText,
  completed,
  actionRoute,
}: {
  heading: string;
  description: string;
  actionText?: string;
  completed?: boolean;
  actionRoute?: string;
}) => {
  return (
    <div className="flex flex-grow justify-between">
      <div className="mr-2 flex items-center pr-2">
        <IconCheck
          className={classnames('mr-4', completed && 'text-icon-success')}
        />
        <div className={classnames('text-base', completed && 'text-disabled')}>
          <div className="text-lg font-semibold">{heading}</div>
          <div className="text-base">{description}</div>
        </div>
      </div>
      {actionText && actionRoute && (
        <Button id={actionText} variant="primary" as={Link} to={actionRoute}>
          {actionText}
        </Button>
      )}
    </div>
  );
};

const ClickableUrl = ({
  variantUrl,
  graphId,
  graphVariant,
}: {
  variantUrl: string;
  className?: string;
  graphId?: string;
  graphVariant?: string;
}) => {
  const location = useLocation();
  return (
    <div className="mb-2 flex items-center rounded-md border border-primary bg-silver-lighter px-4 py-2 text-base text-secondary">
      {variantUrl && (
        <ClickableText
          as="a"
          href={variantUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-2 text-link"
        >
          {variantUrl}
        </ClickableText>
      )}
      {variantUrl ? (
        <Tooltip
          label="Copy variant url"
          clickedLabel="Copied"
          placement="right"
          className="flex flex-1"
        >
          <IconButton
            id="graphVariantURLCopyButton"
            variant="hidden"
            onClick={() => copy(variantUrl)}
            aria-label="Copy"
          >
            <IconCopy className="bg-transparent text-link" />
          </IconButton>
        </Tooltip>
      ) : (
        <span>
          This graph doesn't have an endpoint yet
          <PermissionGuard
            graphId={graphId}
            graphVariant={graphVariant}
            graphVariantPermissions="canManageExplorerSettings"
            fallback={null}
          >
            <span> - add one in </span>
            <ClickableText
              className="text-link"
              as={
                <Link
                  to={setOverlayVisibleLocation({
                    location,
                    overlayName: Config.modals.connectionSettings,
                  })}
                />
              }
            >
              Connection Settings.
            </ClickableText>{' '}
          </PermissionGuard>
        </span>
      )}
    </div>
  );
};
